import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Fingerprint, KeyRound, Loader2, Plus, Shield } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CopyButton } from '@/components/CopyButton';
import { EmptyState } from '@/components/EmptyState';
import { ApiError } from '@/lib/fetcher';
import {
  type BatonMetaData,
  type BatonToken,
  type BatonTokenType,
  type IssueTokenInput,
  batonMetaKey,
  batonTokensKey,
  fetchBatonTokens,
  issueBatonToken,
  revokeBatonToken,
} from '@/lib/queries';

type Status = { label: string; variant: 'success' | 'secondary' | 'destructive' };

function tokenStatus(t: BatonToken): Status {
  if (t.revokedAt) return { label: 'revoked', variant: 'destructive' };
  if (t.expiresAt && new Date(t.expiresAt).getTime() <= Date.now()) {
    return { label: 'expired', variant: 'secondary' };
  }
  // Brief 117 — active is green (healthy), NOT the brand red (so ≠ revoked).
  return { label: 'active', variant: 'success' };
}

const allowsRead = (t: BatonTokenType) => t === 'read' || t === 'read_write';
const allowsWrite = (t: BatonTokenType) => t === 'write' || t === 'read_write';

function cap(used: number, allowed: number | null): string {
  return `${used} / ${allowed ?? '∞'}`;
}

export function TokensTab({ id, meta }: { id: string; meta?: BatonMetaData }) {
  const q = useQuery({
    queryKey: batonTokensKey(id),
    queryFn: () => fetchBatonTokens(id),
  });
  // Brief 130 — token mint/modify/revoke is a write; a tombstoned (or
  // otherwise non-active) baton 410s those at the api. Gate the controls
  // so the rejection surfaces here instead of as a late, ugly 410 on
  // click. The token LIST stays visible regardless — reads are useful for
  // audit on a gone baton. Mirrors the Overview/Trust `isActive` idiom.
  const isActive = meta?.deleteState === 'active';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Collaborator tokens</CardTitle>
            <CardDescription>
              Issue a <code className="text-xs">tok_</code> credential and hand
              it to another agent or developer — they use it in place of the
              baton id at <code className="text-xs">/v1/baton/&#123;token&#125;</code>.
              Revoked tokens stay listed for audit.
            </CardDescription>
          </div>
          {isActive ? <IssueTokenDialog id={id} /> : null}
        </CardHeader>
        <CardContent>
          {!isActive ? (
            <p className="mb-4 text-sm text-muted-foreground">
              This baton is {meta?.deleteState ?? 'not active'} — only an active
              baton can mint or manage tokens. Existing tokens are listed below
              for audit.
            </p>
          ) : null}
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load tokens (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data && q.data.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="No tokens yet"
              description="Issue a collaborator token to share this baton without sharing your account."
              action={isActive ? <IssueTokenDialog id={id} /> : undefined}
            />
          ) : q.data ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Token</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.map((t) => {
                  const status = tokenStatus(t);
                  return (
                    <TableRow key={t.tokenId}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <code className="font-mono text-xs">
                            {t.tokenId.slice(0, 12)}…
                          </code>
                          <CopyButton value={t.tokenId} label="Copy" size="sm" />
                        </div>
                        {t.editUrl ? (
                          <div className="mt-1">
                            <CopyButton
                              value={t.editUrl}
                              label={t.type === 'read_write' ? 'Copy editing link' : 'Copy view link'}
                              size="sm"
                              variant="outline"
                            />
                          </div>
                        ) : null}
                        <div className="mt-1 flex flex-wrap gap-1">
                          {t.requireFingerprint ? (
                            <Badge
                              variant="outline"
                              className="gap-1"
                              title="First use binds the token to the caller's fingerprint."
                            >
                              <Fingerprint className="h-3 w-3" /> fingerprint
                            </Badge>
                          ) : null}
                          {t.ipAllowList.length > 0 ? (
                            <Badge
                              variant="outline"
                              className="gap-1"
                              title={t.ipAllowList.join(', ')}
                            >
                              <Shield className="h-3 w-3" /> IP ×
                              {t.ipAllowList.length}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{t.type}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {allowsRead(t.type) ? (
                          <div>R: {cap(t.readsUsed, t.readsAllowed)}</div>
                        ) : null}
                        {allowsWrite(t.type) ? (
                          <div>W: {cap(t.writesUsed, t.writesAllowed)}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {t.expiresAt
                          ? format(parseISO(t.expiresAt), 'MMM d, yyyy')
                          : 'never'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {t.revokedAt ? (
                          <span className="text-xs text-muted-foreground">
                            revoked{' '}
                            {format(parseISO(t.revokedAt), 'MMM d')}
                          </span>
                        ) : isActive ? (
                          <RevokeTokenButton id={id} token={t} />
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function RevokeTokenButton({ id, token }: { id: string; token: BatonToken }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => revokeBatonToken(id, token.tokenId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batonTokensKey(id) });
      void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    },
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
        >
          Revoke
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke this token?</AlertDialogTitle>
          <AlertDialogDescription>
            <code className="font-mono text-xs">{token.tokenId.slice(0, 16)}…</code>{' '}
            stops working immediately. It stays in the list (marked revoked)
            for audit. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={m.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              m.mutate();
            }}
            disabled={m.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {m.isPending ? 'Revoking…' : 'Revoke'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function IssueTokenDialog({ id }: { id: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [type, setType] = React.useState<BatonTokenType>('read');
  const [readsAllowed, setReadsAllowed] = React.useState('');
  const [writesAllowed, setWritesAllowed] = React.useState('');
  const [expiresInDays, setExpiresInDays] = React.useState('');
  const [requireFingerprint, setRequireFingerprint] = React.useState(false);
  const [ipAllowList, setIpAllowList] = React.useState('');
  const [minted, setMinted] = React.useState<BatonToken | null>(null);

  const reset = () => {
    setType('read');
    setReadsAllowed('');
    setWritesAllowed('');
    setExpiresInDays('');
    setRequireFingerprint(false);
    setIpAllowList('');
    setMinted(null);
  };

  const issue = useMutation({
    mutationFn: () => {
      const input: IssueTokenInput = { type };
      if (allowsRead(type) && readsAllowed.trim() !== '') {
        input.reads_allowed = Math.max(0, Math.trunc(Number(readsAllowed)));
      }
      if (allowsWrite(type) && writesAllowed.trim() !== '') {
        input.writes_allowed = Math.max(0, Math.trunc(Number(writesAllowed)));
      }
      if (expiresInDays.trim() !== '' && Number(expiresInDays) > 0) {
        input.expires_at = new Date(
          Date.now() + Number(expiresInDays) * 86_400_000,
        ).toISOString();
      }
      if (requireFingerprint) input.require_fingerprint = true;
      const cidrs = ipAllowList
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (cidrs.length > 0) input.ip_allow_list = cidrs;
      return issueBatonToken(id, input);
    },
    onSuccess: (token) => {
      setMinted(token);
      void qc.invalidateQueries({ queryKey: batonTokensKey(id) });
      void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    },
  });

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-4 w-4" /> Issue token
        </Button>
      </DialogTrigger>
      <DialogContent>
        {minted ? (
          <>
            <DialogHeader>
              <DialogTitle>Token issued</DialogTitle>
              <DialogDescription>
                Hand the <code className="text-xs">tok_</code> to an agent, or the
                editing/view link to a human — they open it in the browser, no
                account. You can re-view both in the list afterward.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3">
              <code className="flex-1 break-all font-mono text-xs">
                {minted.tokenId}
              </code>
              <CopyButton value={minted.tokenId} label="Copy token" variant="outline" size="sm" />
            </div>
            {minted.editUrl ? (
              <div className="mt-2 flex items-center gap-2 rounded-md border bg-muted/40 p-3">
                <code className="flex-1 break-all font-mono text-xs">{minted.editUrl}</code>
                <CopyButton
                  value={minted.editUrl}
                  label={minted.type === 'read_write' ? 'Copy editing link' : 'Copy view link'}
                  variant="outline"
                  size="sm"
                />
              </div>
            ) : null}
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Issue a collaborator token</DialogTitle>
              <DialogDescription>
                A <code className="text-xs">tok_</code> bearer credential scoped
                to this baton. Blank caps mean unlimited within the baton's own
                allowance; blank expiry uses the default (capped at the baton's
                expiry).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="tok-type">Type</Label>
                <select
                  id="tok-type"
                  value={type}
                  onChange={(e) => setType(e.target.value as BatonTokenType)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="read">read</option>
                  <option value="write">write</option>
                  <option value="read_write">read_write</option>
                </select>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {allowsRead(type) ? (
                  <div className="space-y-1">
                    <Label htmlFor="tok-reads">Reads allowed</Label>
                    <Input
                      id="tok-reads"
                      type="number"
                      min="0"
                      placeholder="unlimited"
                      value={readsAllowed}
                      onChange={(e) => setReadsAllowed(e.target.value)}
                    />
                  </div>
                ) : null}
                {allowsWrite(type) ? (
                  <div className="space-y-1">
                    <Label htmlFor="tok-writes">Writes allowed</Label>
                    <Input
                      id="tok-writes"
                      type="number"
                      min="0"
                      placeholder="unlimited"
                      value={writesAllowed}
                      onChange={(e) => setWritesAllowed(e.target.value)}
                    />
                  </div>
                ) : null}
                <div className="space-y-1">
                  <Label htmlFor="tok-expiry">Expires in (days)</Label>
                  <Input
                    id="tok-expiry"
                    type="number"
                    min="1"
                    placeholder="default"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="tok-ip">IP allow-list (optional)</Label>
                <Input
                  id="tok-ip"
                  placeholder="CIDRs, comma-separated — e.g. 203.0.113.0/24"
                  value={ipAllowList}
                  onChange={(e) => setIpAllowList(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={requireFingerprint}
                  onChange={(e) => setRequireFingerprint(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Require fingerprint (bind to first caller)
              </label>
              {issue.error ? (
                <Alert variant="destructive">
                  <AlertTitle>
                    {issue.error instanceof ApiError
                      ? issue.error.code
                      : 'Failed'}
                  </AlertTitle>
                  <AlertDescription>
                    {issue.error instanceof ApiError
                      ? issue.error.message
                      : 'Try again.'}
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
            <DialogFooter>
              <Button onClick={() => issue.mutate()} disabled={issue.isPending}>
                {issue.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Issuing…
                  </>
                ) : (
                  'Issue token'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
